import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	FormControl,
	Grid,
	IconButton,
	MenuItem,
	Select,
	TextField,
} from '@mui/material';

import React, { useEffect } from 'react';

import { debounce } from 'lodash';

import useTableQuery from 'hooks/useTableQuery';

import { nextPaginationIcon, prevPaginationIcon } from '../../assets/images';
import { ImgTagComponent } from '../../utils/utilFunctions';

import { Order, onPageTextChanged } from '../Table/CommonTableStyles';

import stylesCardList from './CardTable.module.scss';

export interface ICardTableProps {
	id: string;
	rows: any;
	defaultSortKey?: string; // default sort key
	totalItems: number;
	searchKey?: string;
	isLoading?: boolean;
	columns?: number;
	totalPagesCount?: number;
	queryPageSize?: number;
	handlePagination: (rowData: any) => void;
	render: (rowData: any) => React.ReactElement;
	showSelectPageOption?: boolean;
	defaultQuery?: any;
}

const CardTable = (props: ICardTableProps) => {
	const {
		id,
		rows,
		defaultSortKey,
		totalItems,
		handlePagination,
		render,
		queryPageSize = 12,
		columns = 4,
		showSelectPageOption,
		defaultQuery,
	} = props;

	const { tableQueryObj, updateTableQuery } = useTableQuery({ id });
	const queryPageNum = defaultQuery?.pageNumber || 0;
	const queryGridPageSize = defaultQuery?.pageSize || queryPageSize;
	const [order] = React.useState<Order>('asc');
	const [orderBy] = React.useState<keyof any>(defaultSortKey || '');
	const [page, setPage] = React.useState(queryPageNum);
	const [rowsPerPage, setRowsPerPage] = React.useState(queryGridPageSize);
	const [textFieldPage, setTextFieldPage] = React.useState<any>(
		queryPageNum + 1,
	);

	const handlePageChange = (data: any) => {
		updateTableQuery(data);
		handlePagination(data);
	};

	const handlePrevPagination = (cPage: number) => {
		setTextFieldPage(cPage - 1);
		setPage(page - 1);
		const pageObj = {
			pageSize: rowsPerPage,
			pageNumber: page - 1,
			sortKey: orderBy,
			sortDirection: order,
		};
		handlePageChange(pageObj);
	};

	const handleNextPagination = (cPage: number) => {
		setPage(cPage);
		setTextFieldPage(cPage + 1);
		const pageObj = {
			pageSize: rowsPerPage,
			pageNumber: cPage,
			sortKey: orderBy,
			sortDirection: order,
		};
		handlePageChange(pageObj);
	};

	const onPageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value, 10);
		const resultValue = onPageTextChanged(value, rowsPerPage, totalItems);
		const paramData = { pageNumber: resultValue - 1 };
		setPage(resultValue);
		setTextFieldPage(resultValue);
		const pageObj = {
			pageSize: rowsPerPage,
			pageNumber: paramData?.pageNumber,
			sortKey: orderBy,
			sortDirection: order,
		};
		handlePagination(pageObj);
	};

	const handleShowEndPage = () => {
		if (totalItems > 0) {
			const val = totalItems / rowsPerPage;
			if (val % 1 !== 0) {
				return Math.trunc(val + 1);
			}
			return totalItems / rowsPerPage;
		}
		return totalItems / rowsPerPage;
	};

	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const pagesize = parseInt(event.target.value, 10);
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		setTextFieldPage(1);
		const pageObj = {
			pageSize: pagesize,
			pageNumber: 0,
			sortKey: orderBy,
			sortDirection: order,
		};
		const tableObj = { pageSize: pagesize, pageNumber: 0 };
		updateTableQuery(tableObj);
		handlePagination(pageObj);
	};
	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setTextFieldPage(event.target.value);
	};

	const debouncedOnPageSelect = debounce(onPageSelect, 500);

	const handleDebouncedChange = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		handleChange(event);
		debouncedOnPageSelect(event);
	};
	useEffect(() => {
		const pageInfo = { ...tableQueryObj };
		pageInfo.pageSize = rowsPerPage;
		updateTableQuery(pageInfo);
		handleShowEndPage();
	}, [rowsPerPage]);

	useEffect(() => {
		if (defaultQuery?.pageNumber === 0) {
			setTextFieldPage(1);
		}
		setPage(queryPageNum);
		setRowsPerPage(queryGridPageSize);
	}, [defaultQuery]);
	return (
		<>
			<Grid
				container
				className={stylesCardList['mapping-card-grid']}
				data-testid="mapping-card-grid"
			>
				{rows &&
					rows.map((res: any) => {
						const renderRow = render(res);
						return (
							<Grid
								item
								key={res.id}
								xs={12}
								sm={12}
								md={12 / columns}
								lg={12 / columns}
								xl={12 / columns}
								p={2}
							>
								{renderRow}
							</Grid>
						);
					})}
			</Grid>
			{rows?.length > 0 && (
				<Grid container xs={12}>
					{!showSelectPageOption ? (
						<Grid
							item
							xs={6}
							className={stylesCardList['common-table-rows-per-page']}
						/>
					) : (
						<Grid
							item
							xs={6}
							className={stylesCardList['common-table-rows-per-page']}
						>
							<FormControl
								variant="standard"
								sx={{ m: 1, minWidth: 120 }}
								className={stylesCardList['common-table-form-control']}
							>
								<span className={stylesCardList['common-table-show-text']}>
									Show
								</span>
								<Select
									type="listbox"
									data-testid="servertable-pages-selectdropdown"
									value={rowsPerPage}
									IconComponent={ExpandMoreRoundedIcon}
									inputProps={{ 'data-testid': 'servertable-pages-select' }}
									onChange={(e: any) => handleChangeRowsPerPage(e)}
									className={stylesCardList['common-table-select']}
									id="demo-simple-select-standard"
								>
									<MenuItem value={12}>12</MenuItem>
									<MenuItem value={24}>24</MenuItem>
									<MenuItem value={48}>48</MenuItem>
								</Select>
								<span className={stylesCardList['common-table-show-text']}>
									items
								</span>
							</FormControl>
						</Grid>
					)}
					<Grid
						item
						xs={6}
						className={stylesCardList['common-pagination-pages-grid']}
					>
						<IconButton
							aria-label="delete"
							data-testid="servertable-prev-icon"
							onClick={() => {
								handlePrevPagination(page + 1);
							}}
							className={stylesCardList['common-pagination-icon-button-prev']}
							disabled={page === 0}
						>
							{ImgTagComponent(
								prevPaginationIcon,
								'prevPaginationIcon',
								'prev-pagination-styles',
							)}
						</IconButton>
						<TextField
							id="outlined-size-small"
							value={textFieldPage}
							size="small"
							className={
								stylesCardList['common-pagination-pages-grid-input-field']
							}
							onChange={(e: any) => handleDebouncedChange(e)}
							inputProps={{
								'data-testid': 'servertable-pagenumber-textfield',
								disabled: totalItems <= rowsPerPage || handleShowEndPage() <= 1,
								max: handleShowEndPage(),
							}}
						/>
						of {handleShowEndPage()}
						<IconButton
							aria-label="delete"
							data-testid="servertable-next-icon"
							onClick={() => {
								handleNextPagination(page + 1);
							}}
							className={stylesCardList['common-pagination-icon-button-next']}
							disabled={page + 1 === handleShowEndPage()}
						>
							{ImgTagComponent(
								nextPaginationIcon,
								'nextPaginationIcon',
								'next-pagination-styles',
							)}
						</IconButton>
					</Grid>
				</Grid>
			)}
		</>
	);
};

export default CardTable;
